/**
 * DiscoveryCallCTA — Prominent CTA section for all main pages.
 * "Talk to our team" with phone and WhatsApp link.
 */
import { Phone, MessageCircle, Calendar } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { Link } from 'react-router-dom';

const CTA_I18N: Record<string, { heading: string; sub: string; phone: string; whatsapp: string; book: string }> = {
  en: { heading: 'Talk to our team', sub: 'Speak with our BPO & IT team about your need — we answer your questions.', phone: 'Call Us', whatsapp: 'WhatsApp Us', book: 'Send us a message' },
  fr: { heading: 'Parlez à notre équipe', sub: 'Échangez avec notre équipe BPO & IT sur votre besoin — nous répondons à vos questions.', phone: 'Appelez-nous', whatsapp: 'WhatsApp', book: 'Nous écrire' },
  es: { heading: 'Hable con nuestro equipo', sub: 'Hable con nuestro equipo BPO & IT sobre su necesidad — respondemos a sus preguntas.', phone: 'Llámenos', whatsapp: 'WhatsApp', book: 'Escríbanos' },
  de: { heading: 'Sprechen Sie mit unserem Team', sub: 'Sprechen Sie mit unserem BPO- & IT-Team über Ihren Bedarf — wir beantworten Ihre Fragen.', phone: 'Anrufen', whatsapp: 'WhatsApp', book: 'Nachricht senden' },
  ar: { heading: 'تحدث إلى فريقنا', sub: 'تحدث مع فريق BPO و IT عن احتياجك — نجيب عن أسئلتك.', phone: 'اتصل بنا', whatsapp: 'واتساب', book: 'راسلنا' },
  it: { heading: 'Parla con il nostro team', sub: 'Parla con il nostro team BPO & IT della tua esigenza — rispondiamo alle tue domande.', phone: 'Chiamaci', whatsapp: 'WhatsApp', book: 'Scrivici' },
  pt: { heading: 'Fale com a nossa equipa', sub: 'Fale com a nossa equipa BPO & IT sobre a sua necessidade — respondemos às suas perguntas.', phone: 'Ligue', whatsapp: 'WhatsApp', book: 'Escreva-nos' },
  nl: { heading: 'Spreek met ons team', sub: 'Spreek met ons BPO- & IT-team over uw behoefte — wij beantwoorden uw vragen.', phone: 'Bel Ons', whatsapp: 'WhatsApp', book: 'Stuur een bericht' },
  pl: { heading: 'Porozmawiaj z naszym zespołem', sub: 'Porozmawiaj z naszym zespołem BPO & IT o swoich potrzebach — odpowiemy na pytania.', phone: 'Zadzwoń', whatsapp: 'WhatsApp', book: 'Napisz do nas' },
  ru: { heading: 'Свяжитесь с нашей командой', sub: 'Обсудите вашу задачу с нашей командой BPO & IT — мы ответим на ваши вопросы.', phone: 'Позвонить', whatsapp: 'WhatsApp', book: 'Написать нам' },
  zh: { heading: '与我们的团队沟通', sub: '向我们的 BPO 与 IT 顾问说明您的需求，我们会回复您。', phone: '致电我们', whatsapp: 'WhatsApp', book: '给我们留言' },
  ja: { heading: '担当チームにご相談ください', sub: 'BPO・IT の担当者がご要件を伺い、ご回答いたします。', phone: 'お電話', whatsapp: 'WhatsApp', book: 'メッセージを送る' },
  ko: { heading: '담당 팀과 상담하십시오', sub: 'BPO·IT 담당자가 요구 사항을 듣고 답변드립니다.', phone: '전화하기', whatsapp: 'WhatsApp', book: '메시지 보내기' },
};

const PHONE_MOROCCO = '+212537373777';
const PHONE_MA = '+212658380381';
const WHATSAPP_URL = `https://wa.me/${PHONE_MA.replace(/[^0-9]/g, '')}`;

export function DiscoveryCallCTA() {
  const { language, isRTL } = useLanguage();
  const t = CTA_I18N[language] || CTA_I18N.en;

  return (
    <section
      dir={isRTL ? 'rtl' : 'ltr'}
      className="bg-primary/5 border-y border-primary/10 py-12 md:py-16"
      aria-label={t.heading}
    >
      <div className="container max-w-4xl text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
          <Calendar className="inline-block w-7 h-7 mr-2 text-primary align-middle" aria-hidden="true" />
          {t.heading}
        </h2>
        <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">{t.sub}</p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={`tel:${PHONE_MOROCCO}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold shadow-lg hover:opacity-90 transition-opacity"
          >
            <Phone className="w-5 h-5" aria-hidden="true" />
            {t.phone}: +212 5 37 37 37 77
          </a>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-green-700 text-white font-semibold shadow-lg hover:opacity-90 transition-opacity"
          >
            <MessageCircle className="w-5 h-5" aria-hidden="true" />
            {t.whatsapp}
          </a>

          <Link
            to={`/${language}/contact`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-primary/10 transition-colors"
          >
            <MessageCircle className="w-5 h-5" aria-hidden="true" />
            {t.book}
          </Link>
        </div>
      </div>
    </section>
  );
}
